/**
 * Flat CRT finish: soft-threshold bloom at 1/2 and 1/4 resolution, light scanlines, a
 * touch of grain and a gentle vignette. No curvature, no bezel, no chromatic aberration.
 */
import { bindTarget, freeTarget, FULLSCREEN_VS, makeFullscreenVao, makeProgram, makeTarget, type GL, type Program, type Target } from './gl';

const PREFILTER_FS = /* glsl */ `#version 300 es
precision highp float;
uniform sampler2D uSrc;
uniform vec2 uTexel;
in vec2 vUv;
out vec4 o;
void main() {
  // 4-tap box downsample + soft threshold
  vec3 c = texture(uSrc, vUv + uTexel * vec2(-0.5, -0.5)).rgb + texture(uSrc, vUv + uTexel * vec2(0.5, -0.5)).rgb
         + texture(uSrc, vUv + uTexel * vec2(-0.5, 0.5)).rgb + texture(uSrc, vUv + uTexel * vec2(0.5, 0.5)).rgb;
  c *= 0.25;
  float l = max(c.r, max(c.g, c.b));
  float k = max(0.0, l - 0.22) / max(l, 1e-4) / 0.78;
  o = vec4(c * k, 1.0);
}`;

const DOWN_FS = /* glsl */ `#version 300 es
precision highp float;
uniform sampler2D uSrc;
uniform vec2 uTexel;
in vec2 vUv;
out vec4 o;
void main() {
  vec3 c = texture(uSrc, vUv + uTexel * vec2(-1.0, -1.0)).rgb + texture(uSrc, vUv + uTexel * vec2(1.0, -1.0)).rgb
         + texture(uSrc, vUv + uTexel * vec2(-1.0, 1.0)).rgb + texture(uSrc, vUv + uTexel * vec2(1.0, 1.0)).rgb;
  o = vec4(c * 0.25, 1.0);
}`;

const BLUR_FS = /* glsl */ `#version 300 es
precision highp float;
uniform sampler2D uSrc;
uniform vec2 uDir;   // texel step along the blur axis
in vec2 vUv;
out vec4 o;
void main() {
  // 9-tap gaussian folded into 5 linear fetches
  vec3 c = texture(uSrc, vUv).rgb * 0.2270270;
  c += texture(uSrc, vUv + uDir * 1.3846154).rgb * 0.3162162;
  c += texture(uSrc, vUv - uDir * 1.3846154).rgb * 0.3162162;
  c += texture(uSrc, vUv + uDir * 3.2307692).rgb * 0.0702703;
  c += texture(uSrc, vUv - uDir * 3.2307692).rgb * 0.0702703;
  o = vec4(c, 1.0);
}`;

const COMPOSITE_FS = /* glsl */ `#version 300 es
precision highp float;
uniform sampler2D uScene;
uniform sampler2D uBloomA;
uniform sampler2D uBloomB;
uniform vec2 uView;
uniform float uDpr;
uniform float uTime;
uniform float uFlash;   // detonation whiteout 0..1
uniform float uGlitch;  // an ally's betrayal: torn scan bands and a red cast, 1 → 0
in vec2 vUv;
out vec4 o;
float hash(vec2 p) { vec3 p3 = fract(vec3(p.xyx) * 0.1031); p3 += dot(p3, p3.yzx + 33.33); return fract((p3.x + p3.y) * p3.z); }
void main() {
  vec2 uv = vUv;
  if (uGlitch > 0.0) {
    // a few bands of scanlines slip sideways, re-rolled 20× a second
    float roll = floor(uTime * 20.0);
    float band = floor(gl_FragCoord.y / (7.0 * uDpr));
    float torn = step(0.78, hash(vec2(band, roll)));
    uv.x += (hash(vec2(band * 1.7, roll + 3.0)) - 0.5) * 0.035 * uGlitch * torn;
  }
  vec3 c = texture(uScene, uv).rgb;
  c += texture(uBloomA, uv).rgb * 0.6 + texture(uBloomB, uv).rgb * 0.75;
  c += vec3(1.0, 0.95, 0.9) * uFlash * 0.12;
  c += vec3(1.0, 0.12, 0.08) * uGlitch * 0.07;
  // scanlines: every third css row slightly darker
  float row = floor((uView.y - gl_FragCoord.y) / max(1.0, floor(uDpr + 0.25)));
  c *= mod(row, 3.0) > 1.5 ? 0.82 : 1.0;
  // vignette
  vec2 v = vUv * 2.0 - 1.0;
  c *= 1.0 - 0.16 * pow(min(1.0, v.x * v.x * 0.6 + v.y * v.y * 0.8), 1.5);
  // grain
  float g = (hash(gl_FragCoord.xy + fract(uTime * 7.0) * 113.0) - 0.5) * (4.0 / 255.0);
  c += vec3(g) + vec3(1.0, 3.0, 2.0) / 255.0;
  o = vec4(c, 1.0);
}`;

export class CrtPost {
  private pre: Program;
  private down: Program;
  private blur: Program;
  private comp: Program;
  private vao: WebGLVertexArrayObject;
  scene: Target | null = null;
  private h1: Target | null = null;
  private h2: Target | null = null;
  private q1: Target | null = null;
  private q2: Target | null = null;

  constructor(private gl: GL) {
    this.pre = makeProgram(gl, 'bloomPre', FULLSCREEN_VS, PREFILTER_FS, ['uSrc', 'uTexel']);
    this.down = makeProgram(gl, 'bloomDown', FULLSCREEN_VS, DOWN_FS, ['uSrc', 'uTexel']);
    this.blur = makeProgram(gl, 'bloomBlur', FULLSCREEN_VS, BLUR_FS, ['uSrc', 'uDir']);
    this.comp = makeProgram(gl, 'crt', FULLSCREEN_VS, COMPOSITE_FS, ['uScene', 'uBloomA', 'uBloomB', 'uView', 'uDpr', 'uTime', 'uFlash', 'uGlitch']);
    this.vao = makeFullscreenVao(gl, this.pre.prog);
  }

  /** Make sure targets match the drawing buffer; returns the scene target to render into. */
  begin(w: number, h: number): Target {
    const gl = this.gl;
    if (!this.scene || this.scene.w !== w || this.scene.h !== h) {
      for (const t of [this.scene, this.h1, this.h2, this.q1, this.q2]) freeTarget(gl, t);
      this.scene = makeTarget(gl, w, h);
      const hw = Math.max(1, w >> 1);
      const hh = Math.max(1, h >> 1);
      this.h1 = makeTarget(gl, hw, hh);
      this.h2 = makeTarget(gl, hw, hh);
      this.q1 = makeTarget(gl, Math.max(1, hw >> 1), Math.max(1, hh >> 1));
      this.q2 = makeTarget(gl, Math.max(1, hw >> 1), Math.max(1, hh >> 1));
    }
    bindTarget(gl, this.scene);
    return this.scene;
  }

  private pass(p: Program, src: Target, dst: Target | null, set: () => void, w = 0, h = 0): void {
    const gl = this.gl;
    bindTarget(gl, dst, w, h);
    gl.useProgram(p.prog);
    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, src.tex);
    gl.uniform1i(p.u.uSrc ?? p.u.uScene!, 0);
    set();
    gl.bindVertexArray(this.vao);
    gl.drawArrays(gl.TRIANGLES, 0, 3);
  }

  finish(w: number, h: number, dpr: number, time: number, flash: number, noBloom = false, glitch = 0): void {
    const gl = this.gl;
    const scene = this.scene!;
    const h1 = this.h1!;
    const h2 = this.h2!;
    const q1 = this.q1!;
    const q2 = this.q2!;
    if (!noBloom) {
      this.pass(this.pre, scene, h1, () => gl.uniform2f(this.pre.u.uTexel!, 1 / scene.w, 1 / scene.h));
      this.pass(this.blur, h1, h2, () => gl.uniform2f(this.blur.u.uDir!, 1 / h1.w, 0));
      this.pass(this.blur, h2, h1, () => gl.uniform2f(this.blur.u.uDir!, 0, 1 / h1.h));
      this.pass(this.down, h1, q1, () => gl.uniform2f(this.down.u.uTexel!, 1 / h1.w, 1 / h1.h));
      this.pass(this.blur, q1, q2, () => gl.uniform2f(this.blur.u.uDir!, 1.6 / q1.w, 0));
      this.pass(this.blur, q2, q1, () => gl.uniform2f(this.blur.u.uDir!, 0, 1.6 / q1.h));
    }
    this.pass(
      this.comp,
      scene,
      null,
      () => {
        gl.activeTexture(gl.TEXTURE1);
        gl.bindTexture(gl.TEXTURE_2D, h1.tex);
        gl.uniform1i(this.comp.u.uBloomA!, 1);
        gl.activeTexture(gl.TEXTURE2);
        gl.bindTexture(gl.TEXTURE_2D, q1.tex);
        gl.uniform1i(this.comp.u.uBloomB!, 2);
        gl.uniform2f(this.comp.u.uView!, w, h);
        gl.uniform1f(this.comp.u.uDpr!, dpr);
        gl.uniform1f(this.comp.u.uTime!, time);
        gl.uniform1f(this.comp.u.uFlash!, flash);
        gl.uniform1f(this.comp.u.uGlitch!, glitch);
      },
      w,
      h,
    );
    gl.bindVertexArray(null);
  }
}
